import React, { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { 
  FileText, Upload, CheckCircle, XCircle, AlertTriangle, 
  ChevronDown, ChevronUp, Star, Sparkles,
  GraduationCap, BookOpen, FileCheck, Loader2, FileDown
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SCHOOLS = [
  { id: 'eth_zurich', name: 'ETH Zürich', icon: '🎓' },
  { id: 'uzh', name: 'Universität Zürich', icon: '📚' },
  { id: 'zhaw', name: 'ZHAW', icon: '🏛️' },
  { id: 'gymnasium', name: 'Gymnasium (Maturarbeit)', icon: '📝' },
  { id: 'custom', name: 'Eigene Vorgaben', icon: '⚙️' }
];

const SeverityBadge = ({ severity }) => {
  const colors = {
    high: 'bg-red-100 text-red-700 border-red-200',
    medium: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    low: 'bg-blue-100 text-blue-700 border-blue-200'
  };
  const labels = { high: 'Hoch', medium: 'Mittel', low: 'Niedrig' };
  
  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded-full border ${colors[severity] || colors.low}`}>
      {labels[severity] || severity}
    </span>
  );
};

const ScoreRing = ({ score }) => {
  const getColor = (s) => {
    if (s >= 90) return '#22c55e';
    if (s >= 70) return '#eab308';
    return '#ef4444';
  };
  
  const circumference = 2 * Math.PI * 45;
  const strokeDashoffset = circumference - (score / 100) * circumference;
  
  return (
    <div className="relative w-32 h-32">
      <svg className="w-full h-full transform -rotate-90">
        <circle
          cx="64"
          cy="64"
          r="45"
          stroke="#e5e7eb"
          strokeWidth="8"
          fill="none"
        />
        <circle
          cx="64"
          cy="64"
          r="45"
          stroke={getColor(score)}
          strokeWidth="8"
          fill="none"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          className="transition-all duration-1000 ease-out"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-3xl font-bold" style={{ color: getColor(score) }}>{score}</span>
        <span className="text-xs text-gray-500">von 100</span>
      </div>
    </div>
  );
};

const IssueCard = ({ issue, index }) => {
  const [expanded, setExpanded] = useState(false);
  
  const icons = {
    margin: <FileText className="w-4 h-4" />,
    font: <BookOpen className="w-4 h-4" />,
    line_spacing: <FileText className="w-4 h-4" />,
    page_numbers: <FileCheck className="w-4 h-4" />,
    missing_section: <AlertTriangle className="w-4 h-4" />,
    missing_toc: <BookOpen className="w-4 h-4" />,
    numbering_gap: <FileText className="w-4 h-4" />,
    citation_inconsistent: <BookOpen className="w-4 h-4" />,
    no_citations: <AlertTriangle className="w-4 h-4" />,
    missing_references: <XCircle className="w-4 h-4" />
  };
  
  return (
    <div 
      className={`border rounded-lg p-4 cursor-pointer transition-all hover:shadow-md ${
        issue.severity === 'high' ? 'border-red-200 bg-red-50' :
        issue.severity === 'medium' ? 'border-yellow-200 bg-yellow-50' :
        'border-blue-200 bg-blue-50'
      }`}
      onClick={() => setExpanded(!expanded)}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className={`p-2 rounded-lg ${
            issue.severity === 'high' ? 'bg-red-100 text-red-600' :
            issue.severity === 'medium' ? 'bg-yellow-100 text-yellow-600' :
            'bg-blue-100 text-blue-600'
          }`}>
            {icons[issue.type] || <AlertTriangle className="w-4 h-4" />}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <SeverityBadge severity={issue.severity} />
              <span className="text-xs text-gray-500 uppercase">{issue.type?.replace('_', ' ')}</span>
            </div>
            <p className="text-sm font-medium text-gray-800">{issue.message}</p>
          </div>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </div>
      
      {expanded && issue.fix && (
        <div className="mt-3 pt-3 border-t border-gray-200">
          <p className="text-sm text-gray-600">
            <span className="font-medium text-green-600">💡 Lösung:</span> {issue.fix}
          </p>
        </div>
      )}
    </div>
  );
};

const ThesisChecker = () => {
  const [selectedSchool, setSelectedSchool] = useState('eth_zurich');
  const [file, setFile] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  // Premium code state
  const [premiumCode, setPremiumCode] = useState('');
  const [premiumValidation, setPremiumValidation] = useState(null);
  const [validatingCode, setValidatingCode] = useState(false);
  const [premiumInfo, setPremiumInfo] = useState(null);
  
  // Load premium info on mount
  useEffect(() => {
    fetch(`${API_URL}/api/thesis-checker/premium/info`)
      .then(res => res.json())
      .then(data => setPremiumInfo(data))
      .catch(err => console.error('Failed to load premium info:', err));
  }, []);
  
  const validatePremiumCode = async () => {
    if (!premiumCode.trim()) return;
    
    setValidatingCode(true);
    try {
      const response = await fetch(`${API_URL}/api/thesis-checker/premium/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: premiumCode.trim() })
      });
      const data = await response.json();
      setPremiumValidation(data);
    } catch (err) {
      setPremiumValidation({ valid: false, message: 'Validierung fehlgeschlagen' });
    } finally {
      setValidatingCode(false);
    }
  };
  
  const consumePremiumCode = async () => {
    if (!premiumCode.trim()) return;
    
    try {
      await fetch(`${API_URL}/api/thesis-checker/premium/use`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: premiumCode.trim() })
      });
    } catch (err) {
      console.error('Failed to use premium code:', err);
    }
  };
  
  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      const f = acceptedFiles[0];
      if (f.name.endsWith('.docx') || f.name.endsWith('.pdf')) {
        setFile(f);
        setError(null);
        setResult(null);
      } else {
        setError('Bitte laden Sie eine Word (.docx) oder PDF (.pdf) Datei hoch.');
      }
    }
  }, []);
  
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx']
    },
    maxFiles: 1
  });
  
  const analyzeDocument = async () => {
    if (!file) return;
    
    setAnalyzing(true);
    setError(null);
    
    // Check if premium code is valid
    const isPremium = premiumValidation?.valid === true;
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('school', selectedSchool);
      formData.append('is_premium', isPremium.toString());
      
      const response = await fetch(`${API_URL}/api/thesis-checker/analyze`, {
        method: 'POST',
        body: formData
      });
      
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Analyse fehlgeschlagen');
      }
      
      const data = await response.json();
      
      // If premium was used, decrement the usage counter
      if (isPremium && data.ai_suggestions?.length > 0 && data.ai_suggestions[0].type !== 'premium') {
        await consumePremiumCode();
        // Refresh validation to show updated remaining uses
        validatePremiumCode();
      }
      
      setResult(data);
      setActiveTab('overview');
    } catch (err) {
      setError(err.message);
    } finally {
      setAnalyzing(false);
    }
  };
  
  const resetAnalysis = () => {
    setFile(null);
    setResult(null);
    setError(null);
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <GraduationCap className="w-4 h-4" />
            Kostenlos für alle Studierenden
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            Arbeit-Checker
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Prüfen Sie Ihre Abschlussarbeit auf Formatierung, Struktur und Zitierweise 
            – automatisch nach den Vorgaben Ihrer Hochschule.
          </p>
          <a 
            href="/thesis-checker/demo" 
            className="inline-flex items-center gap-2 mt-4 text-sm text-green-700 hover:text-green-800 underline"
          >
            📦 Widget für adagmedia.ch einbetten
          </a>
        </div>
        
        {!result ? (
          <div className="bg-white rounded-2xl shadow-lg p-8">
            {/* School Selection */}
            <div className="mb-8">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                1. Wählen Sie Ihre Hochschule
              </label>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {SCHOOLS.map((school) => (
                  <button
                    key={school.id}
                    onClick={() => setSelectedSchool(school.id)}
                    className={`p-4 rounded-xl border-2 transition-all text-center ${
                      selectedSchool === school.id
                        ? 'border-green-500 bg-green-50'
                        : 'border-gray-200 hover:border-green-300 hover:bg-gray-50'
                    }`}
                  >
                    <span className="text-2xl mb-1 block">{school.icon}</span>
                    <span className="text-sm font-medium text-gray-800">{school.name}</span>
                  </button>
                ))}
              </div>
            </div>
            
            {/* File Upload */}
            <div className="mb-8">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                2. Laden Sie Ihre Arbeit hoch
              </label>
              <div
                {...getRootProps()}
                className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-all ${
                  isDragActive 
                    ? 'border-green-500 bg-green-50' 
                    : file 
                      ? 'border-green-500 bg-green-50' 
                      : 'border-gray-300 hover:border-green-400 hover:bg-gray-50'
                }`}
              >
                <input {...getInputProps()} />
                {file ? (
                  <div className="flex flex-col items-center">
                    <CheckCircle className="w-12 h-12 text-green-500 mb-3" />
                    <p className="text-lg font-medium text-gray-800">{file.name}</p>
                    <p className="text-sm text-gray-500 mt-1">
                      {(file.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                    <button 
                      onClick={(e) => { e.stopPropagation(); resetAnalysis(); }}
                      className="mt-3 text-sm text-red-600 hover:text-red-700"
                    >
                      Andere Datei wählen
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col items-center">
                    <Upload className="w-12 h-12 text-gray-400 mb-3" />
                    <p className="text-lg font-medium text-gray-600">
                      {isDragActive ? 'Datei hier ablegen...' : 'Datei hierher ziehen'}
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                      oder klicken zum Auswählen (Word & PDF)
                    </p>
                  </div>
                )}
              </div>
            </div>
            
            {/* Premium Option - Code Entry */}
            <div className="mb-8 p-5 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-200">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-5 h-5 text-purple-500" />
                <span className="font-semibold text-gray-800">Premium-Analyse</span>
                <span className="bg-purple-100 text-purple-700 text-xs px-2 py-0.5 rounded-full">CHF 5</span>
              </div>
              
              <p className="text-sm text-gray-600 mb-4">
                Erhalten Sie KI-gestützte Verbesserungsvorschläge für Formulierungen, 
                Struktur und akademischen Stil.
              </p>
              
              {/* Code Input */}
              <div className="flex gap-2 mb-3">
                <input
                  type="text"
                  placeholder="Freischalt-Code (z.B. ADAG-XXXX-XXXX)"
                  value={premiumCode}
                  onChange={(e) => {
                    setPremiumCode(e.target.value.toUpperCase());
                    setPremiumValidation(null);
                  }}
                  className="flex-1 px-4 py-2 border border-purple-200 rounded-lg focus:outline-none focus:border-purple-400 text-sm font-mono"
                  data-testid="premium-code-input"
                />
                <button
                  onClick={validatePremiumCode}
                  disabled={!premiumCode.trim() || validatingCode}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  data-testid="validate-code-button"
                >
                  {validatingCode ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Prüfen'}
                </button>
              </div>
              
              {/* Validation Result */}
              {premiumValidation && (
                <div className={`p-3 rounded-lg text-sm ${
                  premiumValidation.valid 
                    ? 'bg-green-100 text-green-700 border border-green-200' 
                    : 'bg-red-100 text-red-700 border border-red-200'
                }`}>
                  <div className="flex items-center gap-2">
                    {premiumValidation.valid ? (
                      <CheckCircle className="w-4 h-4" />
                    ) : (
                      <XCircle className="w-4 h-4" />
                    )}
                    <span>{premiumValidation.message}</span>
                  </div>
                </div>
              )}
              
              {/* Buy Premium Link */}
              {!premiumValidation?.valid && premiumInfo && (
                <div className="mt-4 pt-4 border-t border-purple-200">
                  <p className="text-sm text-gray-600 mb-2">Noch keinen Code?</p>
                  <a
                    href={premiumInfo.product_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors"
                    data-testid="buy-premium-link"
                  >
                    <Sparkles className="w-4 h-4" />
                    Premium-Analyse kaufen (CHF {premiumInfo.price})
                  </a>
                  <p className="text-xs text-gray-500 mt-2">
                    Nach dem Kauf erhalten Sie einen Freischalt-Code per E-Mail.
                  </p>
                </div>
              )}
            </div>
            
            {/* Error Message */}
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}
            
            {/* Analyze Button */}
            <button
              onClick={analyzeDocument}
              disabled={!file || analyzing}
              data-testid="analyze-button"
              className={`w-full py-4 rounded-xl font-semibold text-lg transition-all flex items-center justify-center gap-3 ${
                file && !analyzing
                  ? 'bg-green-600 hover:bg-green-700 text-white shadow-lg hover:shadow-xl'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              {analyzing ? (
                <>
                  <Loader2 className="w-6 h-6 animate-spin" />
                  Analysiere{premiumValidation?.valid ? ' (mit Premium)' : ''}...
                </>
              ) : (
                <>
                  <FileCheck className="w-6 h-6" />
                  Arbeit analysieren{premiumValidation?.valid ? ' (Premium)' : ''}
                </>
              )}
            </button>
            
            {/* Official Style-Guides — replaces former ADAG templates (2026-06-01) */}
            <div className="mt-8 pt-8 border-t border-gray-200">
              <div className="p-5 bg-blue-50 border border-blue-200 rounded-xl">
                <div className="flex items-start gap-3 mb-3">
                  <FileDown className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-gray-800">Offizielle Hochschul-Style-Guides</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      Wir verteilen keine eigenen Pseudo-Vorlagen — die Reglements ändern sich semesterweise und variieren pro Departement. Nutze immer den verbindlichen Style-Guide deiner Hochschule.
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-3">
                  <a href="https://ethz.ch/students/de/studium/leistungskontrollen/abschlussarbeiten.html" target="_blank" rel="noopener noreferrer" data-testid="style-guide-eth" className="px-3 py-2 bg-white hover:bg-blue-100 border border-blue-200 rounded-lg text-sm text-blue-800 font-medium transition-colors">→ ETH Zürich Style-Guide</a>
                  <a href="https://www.uzh.ch/de/studies/teaching.html" target="_blank" rel="noopener noreferrer" data-testid="style-guide-uzh" className="px-3 py-2 bg-white hover:bg-blue-100 border border-blue-200 rounded-lg text-sm text-blue-800 font-medium transition-colors">→ UZH Vorgaben</a>
                  <a href="https://www.zhaw.ch/de/hochschulbibliothek/schreiben-publizieren" target="_blank" rel="noopener noreferrer" data-testid="style-guide-zhaw" className="px-3 py-2 bg-white hover:bg-blue-100 border border-blue-200 rounded-lg text-sm text-blue-800 font-medium transition-colors">→ ZHAW Schreiben &amp; Publizieren</a>
                  <a href="https://www.zh.ch/de/bildung/mittelschulen.html" target="_blank" rel="noopener noreferrer" data-testid="style-guide-gym" className="px-3 py-2 bg-white hover:bg-blue-100 border border-blue-200 rounded-lg text-sm text-blue-800 font-medium transition-colors">→ Mittelschulen Kanton ZH</a>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Results Header */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                <div className="flex items-center gap-6">
                  <ScoreRing score={result.overall_score} />
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Analyse-Ergebnis</h2>
                    <p className="text-gray-600">{result.school}</p>
                    <div className="flex gap-4 mt-2 text-sm text-gray-500">
                      <span>{result.page_count} Seiten</span>
                      <span>•</span>
                      <span>{result.word_count?.toLocaleString()} Wörter</span>
                      <span>•</span>
                      <span>{result.issues_count} Probleme</span>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <button
                    onClick={resetAnalysis}
                    className="px-6 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-colors"
                  >
                    Neue Analyse
                  </button>
                  <a
                    href={result.print_quote?.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors text-center"
                  >
                    Jetzt drucken lassen
                  </a>
                </div>
              </div>
            </div>
            
            {/* Tabs */}
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <div className="flex border-b">
                {[
                  { id: 'overview', label: 'Übersicht', icon: <FileText className="w-4 h-4" /> },
                  { id: 'formatting', label: 'Formatierung', icon: <FileCheck className="w-4 h-4" /> },
                  { id: 'structure', label: 'Struktur', icon: <BookOpen className="w-4 h-4" /> },
                  { id: 'citations', label: 'Zitationen', icon: <GraduationCap className="w-4 h-4" /> },
                  { id: 'ai', label: 'KI-Tipps', icon: <Sparkles className="w-4 h-4" /> }
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
                      activeTab === tab.id
                        ? 'bg-green-50 text-green-700 border-b-2 border-green-500'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {tab.icon}
                    <span className="hidden sm:inline">{tab.label}</span>
                  </button>
                ))}
              </div>
              
              <div className="p-6">
                {/* Overview Tab */}
                {activeTab === 'overview' && (
                  <div className="space-y-6">
                    {/* Quick Stats */}
                    <div className="grid grid-cols-3 gap-4">
                      <div className="p-4 rounded-xl bg-gradient-to-br from-blue-50 to-blue-100 text-center">
                        <div className="text-3xl font-bold text-blue-600">{result.formatting?.score || 0}%</div>
                        <div className="text-sm text-blue-700">Formatierung</div>
                      </div>
                      <div className="p-4 rounded-xl bg-gradient-to-br from-purple-50 to-purple-100 text-center">
                        <div className="text-3xl font-bold text-purple-600">{result.structure?.score || 0}%</div>
                        <div className="text-sm text-purple-700">Struktur</div>
                      </div>
                      <div className="p-4 rounded-xl bg-gradient-to-br from-orange-50 to-orange-100 text-center">
                        <div className="text-3xl font-bold text-orange-600">{result.citations?.score || 0}%</div>
                        <div className="text-sm text-orange-700">Zitationen</div>
                      </div>
                    </div>
                    
                    {/* Recommendations */}
                    {result.recommendations?.length > 0 && (
                      <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
                        <h3 className="font-semibold text-green-800 mb-2">Empfehlungen</h3>
                        <ul className="space-y-1">
                          {result.recommendations.map((rec, i) => (
                            <li key={i} className="text-sm text-green-700 flex items-start gap-2">
                              <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                              {rec}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {/* All Issues */}
                    <div>
                      <h3 className="font-semibold text-gray-800 mb-4">
                        Alle Probleme ({result.all_issues?.length || 0})
                      </h3>
                      {result.all_issues?.length > 0 ? (
                        <div className="space-y-3">
                          {result.all_issues.map((issue, i) => (
                            <IssueCard key={i} issue={issue} index={i} />
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8 text-gray-500">
                          <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-500" />
                          <p>Keine Probleme gefunden! Ihre Arbeit entspricht den Vorgaben.</p>
                        </div>
                      )}
                    </div>
                    
                    {/* Print Quote */}
                    {result.print_quote && (
                      <div className="p-6 bg-gradient-to-r from-green-600 to-green-700 rounded-xl text-white">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="font-semibold text-lg mb-1">Bereit zum Drucken?</h3>
                            <p className="text-green-100 text-sm">
                              {result.page_count} Seiten • Ab {result.print_quote.estimated_price_softcover}
                            </p>
                          </div>
                          <a
                            href={result.print_quote.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-6 py-3 bg-white text-green-700 rounded-lg font-semibold hover:bg-green-50 transition-colors"
                          >
                            Jetzt bei ADAG drucken
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Formatting Tab */}
                {activeTab === 'formatting' && (
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-gray-800">Formatierung</h3>
                      <span className={`text-2xl font-bold ${
                        result.formatting?.score >= 90 ? 'text-green-600' :
                        result.formatting?.score >= 70 ? 'text-yellow-600' : 'text-red-600'
                      }`}>
                        {result.formatting?.score}%
                      </span>
                    </div>
                    
                    {/* Detected Settings */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Erkannte Schriftarten</h4>
                        <div className="flex flex-wrap gap-2">
                          {result.formatting?.detected?.fonts?.map((font, i) => (
                            <span key={i} className="px-2 py-1 bg-white border rounded text-sm">{font}</span>
                          )) || <span className="text-gray-500 text-sm">Keine erkannt</span>}
                        </div>
                      </div>
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Seitenränder</h4>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <span>Oben: {result.formatting?.detected?.margins?.top?.toFixed(1) || '?'} cm</span>
                          <span>Unten: {result.formatting?.detected?.margins?.bottom?.toFixed(1) || '?'} cm</span>
                          <span>Links: {result.formatting?.detected?.margins?.left?.toFixed(1) || '?'} cm</span>
                          <span>Rechts: {result.formatting?.detected?.margins?.right?.toFixed(1) || '?'} cm</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Issues */}
                    <div className="space-y-3">
                      {result.formatting?.issues?.map((issue, i) => (
                        <IssueCard key={i} issue={issue} index={i} />
                      ))}
                      {(!result.formatting?.issues || result.formatting.issues.length === 0) && (
                        <div className="text-center py-6 text-green-600">
                          <CheckCircle className="w-8 h-8 mx-auto mb-2" />
                          <p>Formatierung entspricht den Vorgaben!</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Structure Tab */}
                {activeTab === 'structure' && (
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-gray-800">Struktur</h3>
                      <span className={`text-2xl font-bold ${
                        result.structure?.score >= 90 ? 'text-green-600' :
                        result.structure?.score >= 70 ? 'text-yellow-600' : 'text-red-600'
                      }`}>
                        {result.structure?.score}%
                      </span>
                    </div>
                    
                    {/* Detected Sections */}
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <h4 className="text-sm font-medium text-gray-700 mb-3">Pflichtabschnitte</h4>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {result.structure?.detected_sections?.map((section, i) => (
                          <div 
                            key={i}
                            className={`flex items-center gap-2 p-2 rounded ${
                              section.status === 'found' 
                                ? 'bg-green-100 text-green-700'
                                : 'bg-red-100 text-red-700'
                            }`}
                          >
                            {section.status === 'found' 
                              ? <CheckCircle className="w-4 h-4" />
                              : <XCircle className="w-4 h-4" />
                            }
                            <span className="text-sm">{section.name}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {/* TOC and Headings Info */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className={`p-4 rounded-lg ${
                        result.structure?.has_toc ? 'bg-green-50' : 'bg-red-50'
                      }`}>
                        <div className="flex items-center gap-2">
                          {result.structure?.has_toc 
                            ? <CheckCircle className="w-5 h-5 text-green-600" />
                            : <XCircle className="w-5 h-5 text-red-600" />
                          }
                          <span className="font-medium">Inhaltsverzeichnis</span>
                        </div>
                      </div>
                      <div className="p-4 rounded-lg bg-gray-50">
                        <div className="text-sm text-gray-600">Überschriften erkannt:</div>
                        <div className="text-2xl font-bold text-gray-800">{result.structure?.total_headings || 0}</div>
                      </div>
                    </div>
                    
                    {/* Issues */}
                    <div className="space-y-3">
                      {result.structure?.issues?.map((issue, i) => (
                        <IssueCard key={i} issue={issue} index={i} />
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Citations Tab */}
                {activeTab === 'citations' && (
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-gray-800">Zitationen & Referenzen</h3>
                      <span className={`text-2xl font-bold ${
                        result.citations?.score >= 90 ? 'text-green-600' :
                        result.citations?.score >= 70 ? 'text-yellow-600' : 'text-red-600'
                      }`}>
                        {result.citations?.score}%
                      </span>
                    </div>
                    
                    {/* Citation Stats */}
                    <div className="grid grid-cols-3 gap-4">
                      <div className="p-4 bg-gray-50 rounded-lg text-center">
                        <div className="text-2xl font-bold text-gray-800">{result.citations?.citation_count || 0}</div>
                        <div className="text-sm text-gray-600">Zitationen gefunden</div>
                      </div>
                      <div className="p-4 bg-gray-50 rounded-lg text-center">
                        <div className="text-lg font-bold text-gray-800">{result.citations?.detected_style || '?'}</div>
                        <div className="text-sm text-gray-600">Erkannter Stil</div>
                      </div>
                      <div className={`p-4 rounded-lg text-center ${
                        result.citations?.has_references ? 'bg-green-50' : 'bg-red-50'
                      }`}>
                        {result.citations?.has_references 
                          ? <CheckCircle className="w-6 h-6 mx-auto text-green-600 mb-1" />
                          : <XCircle className="w-6 h-6 mx-auto text-red-600 mb-1" />
                        }
                        <div className="text-sm">Literaturverzeichnis</div>
                      </div>
                    </div>
                    
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-sm text-blue-700">
                        <strong>Empfohlener Zitierstil:</strong> {result.citations?.preferred_style || 'APA'}
                      </p>
                    </div>
                    
                    {/* Issues */}
                    <div className="space-y-3">
                      {result.citations?.issues?.map((issue, i) => (
                        <IssueCard key={i} issue={issue} index={i} />
                      ))}
                      {(!result.citations?.issues || result.citations.issues.length === 0) && (
                        <div className="text-center py-6 text-green-600">
                          <CheckCircle className="w-8 h-8 mx-auto mb-2" />
                          <p>Zitationen sehen gut aus!</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {/* AI Tab */}
                {activeTab === 'ai' && (
                  <div className="space-y-6">
                    <div className="flex items-center gap-3 mb-4">
                      <Sparkles className="w-6 h-6 text-purple-500" />
                      <h3 className="font-semibold text-gray-800">KI-Verbesserungsvorschläge</h3>
                    </div>
                    
                    {result.ai_suggestions?.map((suggestion, i) => (
                      <div key={i} className={`p-4 rounded-xl border ${
                        suggestion.type === 'premium'
                          ? 'bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200'
                          : suggestion.type === 'ai_suggestion'
                            ? 'bg-white border-gray-200'
                            : 'bg-gray-50 border-gray-200'
                      }`}>
                        {suggestion.type === 'premium' ? (
                          <div className="text-center py-4">
                            <Sparkles className="w-10 h-10 text-purple-400 mx-auto mb-3" />
                            <h4 className="font-semibold text-purple-800 mb-2">{suggestion.message}</h4>
                            <p className="text-sm text-purple-600 mb-4">{suggestion.preview}</p>
                            {premiumInfo && (
                              <a
                                href={premiumInfo.product_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 px-6 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors"
                              >
                                <Sparkles className="w-4 h-4" />
                                Premium kaufen (CHF {premiumInfo.price})
                              </a>
                            )}
                            <p className="text-xs text-purple-500 mt-3">
                              Nach dem Kauf erhalten Sie einen Freischalt-Code per E-Mail.
                              Starten Sie dann eine neue Analyse mit Ihrem Code.
                            </p>
                          </div>
                        ) : (
                          <div className="flex items-start gap-3">
                            <div className="p-2 bg-purple-100 rounded-lg">
                              <Star className="w-4 h-4 text-purple-600" />
                            </div>
                            <p className="text-gray-700 text-sm leading-relaxed">{suggestion.message}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        
        {/* Footer Info */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <p>
            Powered by ADAG Print • Ihre Druckerei in Zürich seit 1994
          </p>
          <p className="mt-1">
            Fragen? <a href="https://adagmedia.ch/uber-uns/kontakt.html" className="text-green-600 hover:underline">Kontaktieren Sie uns</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ThesisChecker;
